import React, { FC, ReactNode, useState } from "react";
import toast from "react-hot-toast";
import { MenuRow } from "../DownloadMenu/DownloadMenuList";
import { MenuPopover, MenuTooltip, MenuTriggerVariant } from "../DownloadMenu/MenuPopover";
import { IconLink } from "../../utils/icons";

export const LinkGlyph: FC<{ size?: number; strokeWidth?: number }> = ({
  size = 18,
  strokeWidth = 1.5,
}) => (
  <span
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 0,
      width: size,
      height: size,
      transform: "rotate(-45deg)",
    }}
  >
    <IconLink size={size} stroke="currentColor" strokeWidth={strokeWidth} />
  </span>
);

const defaultTooltip = (
  <MenuTooltip title="Copy Public URL" body="Copy the public order or project page link" />
);

export interface PublicUrlOption {
  key: string;
  title: string;
  description: string;
  toast: string;
  url: string | null;
}

export const buildPublicUrlOptions = (
  project: {
    id?: number;
    download_page?: boolean | null;
    public_page_token?: string | null;
    order?: { download_page?: boolean | null; public_page_token?: string | null } | null;
  } | null | undefined,
  // Staff and the brand team get the order link too (TDC-1724).
  canCopyOrder: boolean,
): PublicUrlOption[] => {
  const { origin } = window.location;
  const projectPath = project?.public_page_token
    ? `/projects/public/${project.public_page_token}`
    : `/projects/share/${project?.id}`;
  return [
    {
      key: "order",
      title: "Public Order Page",
      description: "Every project on this order",
      toast: "Order link copied",
      url:
        canCopyOrder && project?.order?.download_page && project.order.public_page_token
          ? `${origin}/orders/public/${project.order.public_page_token}`
          : null,
    },
    {
      key: "project",
      title: "Public Project Page",
      description: "This project only",
      toast: "Project link copied",
      url: project?.download_page && project?.id ? `${origin}${projectPath}` : null,
    },
  ];
};

interface Props {
  options: PublicUrlOption[];
  variant?: MenuTriggerVariant;
  label?: string;
  tooltip?: ReactNode;
  glyphSize?: number;
  stopPropagation?: boolean;
}

export const copyPublicUrl = async (url: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (clipboardBlocked) {
    // Fallback: a read-only, off-screen field (no layout jump, no iOS
    // keyboard). execCommand can throw too — report it as not copied.
    const field = document.createElement("textarea");
    field.value = url;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.top = "-1000px";
    field.style.opacity = "0";
    document.body.appendChild(field);
    try {
      field.select();
      return document.execCommand("copy");
    } catch (copyFailed) {
      return false;
    } finally {
      document.body.removeChild(field);
    }
  }
};

export const PublicUrlMenu: FC<Props> = ({
  options,
  variant = "icon",
  label = "Copy Public URL",
  tooltip,
  glyphSize,
  stopPropagation = true,
}) => {
  const [open, setOpen] = useState(false);
  const available = options.filter((option) => !!option.url);
  if (available.length === 0) return null;

  const copy = async (option: PublicUrlOption) => {
    setOpen(false);
    const copied = await copyPublicUrl(option.url as string);
    if (copied) toast.success(option.toast);
    else toast.error("Could not copy the link");
  };
  // One link: the trigger copies it straight away, no menu to open.
  const single = available.length === 1 ? available[0] : null;

  return (
    <MenuPopover
      open={open && !single}
      onToggle={() => (single ? copy(single) : setOpen((v) => !v))}
      onClose={() => setOpen(false)}
      variant={variant}
      label={label}
      tooltip={tooltip ?? defaultTooltip}
      triggerTitle={label}
      glyph={(props) => <LinkGlyph {...props} />}
      glyphSize={glyphSize}
      stopPropagation={stopPropagation}
    >
      <div role="menu" aria-label="Public page links">
        <div
          className="uppercase text-muted"
          style={{
            fontSize: 9.5,
            letterSpacing: ".12em",
            padding: "12px 14px 8px",
            fontFamily: "CircularStdBlack",
          }}
        >
          Copy link
        </div>
        <div className="flex flex-col gap-0.5" style={{ padding: "0 8px 10px" }}>
          {available.map((option) => (
            <MenuRow
              key={option.key}
              glyph={<LinkGlyph size={18} />}
              title={option.title}
              description={option.description}
              disabled={false}
              onClick={() => copy(option)}
            />
          ))}
        </div>
      </div>
    </MenuPopover>
  );
};
